import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTextDomainContext } from "@/app/providers";
import { useQuery, useMutation } from "@apollo/client/react";
import { queries, mutations } from "@/graphql";
import { enqueueSnackbar } from "notistack";

const CHART_OPTIONS = ["Bar Chart", "Line Chart", "Area Chart"];
const NUMBER_OPTIONS = Array.from({ length: 21 }, (_, i) => i);
const YES_NO_OPTIONS = ["Yes", "No"];

export interface SettingItemDef {
  key: string;
  label: string;
  description: string;
  type: "chart-type" | "number" | "yes-no" | "textarea" | "text";
  category: "ssh" | "repository" | "registration" | "chart" | "date";
  defaultValue: string | number;
  placeholder?: string;
}

const getSettingItems = (gettext: (s: string) => string): SettingItemDef[] => [
  // Git SSH Key Settings
  {
    key: "git-private-key",
    label: gettext("Git Private Key"),
    description: gettext(
      "SSH private key used for authenticating Git operations with remote repositories.",
    ),
    type: "textarea",
    category: "ssh",
    defaultValue: "",
    placeholder:
      "-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----",
  },
  {
    key: "git-public-key",
    label: gettext("Git Public Key"),
    description: gettext(
      "SSH public key to register in Git hosting services (e.g., GitHub, Bitbucket, GitLab).",
    ),
    type: "textarea",
    category: "ssh",
    defaultValue: "",
    placeholder:
      "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA... or ssh-rsa AAAAB3NzaC1yc2EAAA...",
  },
  // Git Repository Settings
  {
    key: "www-git-repository",
    label: gettext("WWW Repository URL"),
    description: gettext(
      "Git repository clone/remote URL for the www.fabeke.com website.",
    ),
    type: "text",
    category: "repository",
    defaultValue: "",
    placeholder: "git@bitbucket.org:fabeke/www.fabeke.com.git",
  },
  {
    key: "app-git-repository",
    label: gettext("App Repository URL"),
    description: gettext(
      "Git repository clone/remote URL for the app.fabeke.com web application.",
    ),
    type: "text",
    category: "repository",
    defaultValue: "",
    placeholder: "git@bitbucket.org:fabeke/app.fabeke.com.git",
  },
  {
    key: "api-git-repository",
    label: gettext("API Repository URL"),
    description: gettext(
      "Git repository clone/remote URL for the api.fabeke.com backend API service.",
    ),
    type: "text",
    category: "repository",
    defaultValue: "",
    placeholder: "git@bitbucket.org:fabeke/api.fabeke.com.git",
  },
  // Registration Settings
  {
    key: "allow-registration",
    label: gettext("Allow Registration"),
    description: gettext(
      "Default setting to allow or disallow user registration / sign up across the system.",
    ),
    type: "yes-no",
    category: "registration",
    defaultValue: "Yes",
  },
  // Chart Settings
  {
    key: "energy-chart-type",
    label: gettext("Energy Chart Type"),
    description: gettext(
      "Select the preferred chart visualization type for energy consumption data.",
    ),
    type: "chart-type",
    category: "chart",
    defaultValue: "Bar Chart",
  },
  {
    key: "climate-chart-type",
    label: gettext("Climate Chart Type"),
    description: gettext(
      "Select the preferred chart visualization type for climate and environmental data.",
    ),
    type: "chart-type",
    category: "chart",
    defaultValue: "Bar Chart",
  },
  {
    key: "cost-chart-type",
    label: gettext("Cost Chart Type"),
    description: gettext(
      "Select the preferred chart visualization type for cost and financial analytics.",
    ),
    type: "chart-type",
    category: "chart",
    defaultValue: "Bar Chart",
  },
  {
    key: "budget-chart-type",
    label: gettext("Budget Chart Type"),
    description: gettext(
      "Select the preferred chart visualization type for budget forecasting and analytics.",
    ),
    type: "chart-type",
    category: "chart",
    defaultValue: "Bar Chart",
  },
  // Date Range Limits - Energy
  {
    key: "energy-date-past-years",
    label: gettext("Energy Date Past Years"),
    description: gettext(
      "How many past years can users select on the Energy page",
    ),
    type: "number",
    category: "date",
    defaultValue: 10,
  },
  {
    key: "energy-date-future-years",
    label: gettext("Energy Date Future Years"),
    description: gettext(
      "How many future years can users select on the Energy page",
    ),
    type: "number",
    category: "date",
    defaultValue: 3,
  },
  // Date Range Limits - Climate
  {
    key: "climate-date-past-years",
    label: gettext("Climate Date Past Years"),
    description: gettext(
      "How many past years can the user select on the Climate page",
    ),
    type: "number",
    category: "date",
    defaultValue: 10,
  },
  {
    key: "climate-date-future-years",
    label: gettext("Climate Date Future Years"),
    description: gettext(
      "How many future years can the user select on the Climate page",
    ),
    type: "number",
    category: "date",
    defaultValue: 3,
  },
  // Date Range Limits - Cost
  {
    key: "cost-date-past-years",
    label: gettext("Cost Date Past Years"),
    description: gettext(
      "How many past years can the user select on the Cost page",
    ),
    type: "number",
    category: "date",
    defaultValue: 10,
  },
  {
    key: "cost-date-future-years",
    label: gettext("Cost Date Future Years"),
    description: gettext(
      "How many future years can the user select on the Cost page",
    ),
    type: "number",
    category: "date",
    defaultValue: 3,
  },
  // Date Range Limits - Budget
  {
    key: "budget-date-past-years",
    label: gettext("Budget Date Past Years"),
    description: gettext(
      "How many past years can the user select on the Budget page",
    ),
    type: "number",
    category: "date",
    defaultValue: 10,
  },
  {
    key: "budget-date-future-years",
    label: gettext("Budget Date Future Years"),
    description: gettext(
      "How many future years can the user select on the Budget page",
    ),
    type: "number",
    category: "date",
    defaultValue: 3,
  },
];

const SettingCard = ({
  item,
  currentValue,
  savingKey,
  onValueChange,
  onSaveSingle,
}: {
  item: SettingItemDef;
  currentValue: any;
  savingKey: string | null;
  onValueChange: (key: string, val: any) => void;
  onSaveSingle: (key: string, label: string, val: any) => void;
}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const [showSecret, setShowSecret] = useState(false);

  const { data, loading } = useQuery<{ getSettings: any }>(
    queries.getSettings,
    { variables: { key: item.key } },
  );

  useEffect(() => {
    if (data !== undefined) {
      let parsed = data?.getSettings ?? (data as any)?.settings ?? data;
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "settings" in parsed
      ) {
        parsed = parsed.settings;
      }
      if (item.type === "number") {
        if (typeof parsed === "string") {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            parsed = parseInt(parsed, 10);
          }
        }
        const num = Number(parsed);
        if (!isNaN(num) && num >= 0 && num <= 20) {
          onValueChange(item.key, num);
        } else {
          onValueChange(item.key, item.defaultValue);
        }
      } else if (item.type === "yes-no") {
        let valStr = parsed;
        if (typeof valStr === "boolean") {
          valStr = valStr ? "Yes" : "No";
        } else if (typeof valStr === "string") {
          try {
            const dec = JSON.parse(valStr);
            if (typeof dec === "boolean") {
              valStr = dec ? "Yes" : "No";
            } else if (typeof dec === "string") {
              valStr = dec;
            }
          } catch {
            // keep valStr as is
          }
        }
        if (typeof valStr === "string" && ["Yes", "No"].includes(valStr)) {
          onValueChange(item.key, valStr);
        } else {
          onValueChange(item.key, item.defaultValue);
        }
      } else if (item.type === "textarea" || item.type === "text") {
        let valStr = parsed;
        if (typeof valStr === "string") {
          try {
            const dec = JSON.parse(valStr);
            if (typeof dec === "string") {
              valStr = dec;
            }
          } catch {
            // keep valStr as is
          }
        }
        if (typeof valStr === "string") {
          onValueChange(item.key, valStr);
        } else if (valStr !== null && valStr !== undefined) {
          onValueChange(item.key, String(valStr));
        } else {
          onValueChange(item.key, item.defaultValue ?? "");
        }
      } else {
        if (typeof parsed === "string" && CHART_OPTIONS.includes(parsed)) {
          onValueChange(item.key, parsed);
        } else {
          onValueChange(item.key, item.defaultValue);
        }
      }
    }
  }, [data]);

  const displayVal = currentValue ?? item.defaultValue ?? "";

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 0.5,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {item.label}
          </Typography>
          {(item.key.startsWith("git-") ||
            item.key.includes("-git-repository")) && (
            <Chip
              label={item.key}
              size="small"
              variant="outlined"
              sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
            />
          )}
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {item.description}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : item.type === "textarea" ? (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={5}
              maxRows={10}
              label={item.label}
              placeholder={item.placeholder}
              value={displayVal}
              onChange={(e) => onValueChange(item.key, e.target.value)}
              slotProps={{
                input: {
                  sx: {
                    fontFamily: "monospace",
                    fontSize: "0.82rem",
                    ...(!showSecret &&
                    item.key === "git-private-key" &&
                    displayVal
                      ? {
                          WebkitTextSecurity: "disc",
                        }
                      : {}),
                  },
                },
              }}
            />
          </Box>
        ) : item.type === "text" ? (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={item.label}
              placeholder={item.placeholder}
              value={displayVal}
              onChange={(e) => onValueChange(item.key, e.target.value)}
              slotProps={{
                input: {
                  sx: {
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                  },
                },
              }}
            />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id={`select-label-${item.key}`}>
              {item.label}
            </InputLabel>
            {item.type === "chart-type" ? (
              <Select
                labelId={`select-label-${item.key}`}
                value={displayVal}
                label={item.label}
                onChange={(e) => onValueChange(item.key, e.target.value)}
              >
                {CHART_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {gettext(opt)}
                  </MenuItem>
                ))}
              </Select>
            ) : item.type === "yes-no" ? (
              <Select
                labelId={`select-label-${item.key}`}
                value={displayVal}
                label={item.label}
                onChange={(e) => onValueChange(item.key, e.target.value)}
              >
                {YES_NO_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {gettext(opt)}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                labelId={`select-label-${item.key}`}
                value={displayVal}
                label={item.label}
                onChange={(e) =>
                  onValueChange(item.key, Number(e.target.value))
                }
              >
                {NUMBER_OPTIONS.map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} {num === 1 ? gettext("Year") : gettext("Years")}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        )}
      </CardContent>
      <Box
        sx={{
          p: 2,
          pt: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {(item.type === "textarea" || item.type === "text") && (
            <>
              {item.key === "git-private-key" && (
                <Tooltip
                  title={showSecret ? gettext("Hide key") : gettext("Show key")}
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={!displayVal}
                      onClick={() => setShowSecret((v) => !v)}
                    >
                      {showSecret ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={gettext("Copy to clipboard")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!displayVal}
                    onClick={() => {
                      if (displayVal) {
                        navigator.clipboard
                          .writeText(String(displayVal))
                          .then(() => {
                            enqueueSnackbar(gettext("Copied to clipboard!"), {
                              variant: "info",
                            });
                          });
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          disabled={savingKey === item.key || loading}
          onClick={() => onSaveSingle(item.key, item.label, displayVal)}
        >
          {savingKey === item.key ? (
            <CircularProgress size={16} />
          ) : (
            gettext("Save")
          )}
        </Button>
      </Box>
    </Card>
  );
};

export const SystemSettings = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const settingItems = getSettingItems(gettext);

  const [settingsValues, setSettingsValues] = useState<Record<string, any>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveAllLoading, setSaveAllLoading] = useState<boolean>(false);

  const [updateSettings] = useMutation<any>(mutations.updateSettings, {
    errorPolicy: "all",
  });

  const handleValueChange = (key: string, val: any) => {
    setSettingsValues((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const saveSingleSetting = async (
    key: string,
    label: string,
    valOverride?: any,
  ) => {
    const val = valOverride ?? settingsValues[key] ?? "";
    setSavingKey(key);
    try {
      const res: any = await updateSettings({
        variables: {
          key,
          data: JSON.stringify(val),
        },
        refetchQueries: ["settings"],
      });
      const result = res.data?.updateSettings;
      if (result?.success) {
        enqueueSnackbar(gettext("Setting updated successfully!"), {
          variant: "success",
        });
      } else {
        const errorMsg =
          result?.message?.[0] ||
          res.errors?.[0]?.message ||
          gettext("Failed to save setting.");
        enqueueSnackbar(errorMsg, { variant: "error" });
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || gettext("An error occurred"), {
        variant: "error",
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveAll = async () => {
    setSaveAllLoading(true);
    try {
      for (let i = 0; i < settingItems.length; i++) {
        const item = settingItems[i];
        const val = settingsValues[item.key] ?? item.defaultValue ?? "";
        const isLast = i === settingItems.length - 1;
        await updateSettings({
          variables: {
            key: item.key,
            data: JSON.stringify(val),
          },
          ...(isLast ? { refetchQueries: ["settings"] } : {}),
        });
      }
      enqueueSnackbar(gettext("All system settings saved successfully!"), {
        variant: "success",
      });
    } catch (err: any) {
      enqueueSnackbar(err.message || gettext("Failed to save settings"), {
        variant: "error",
      });
    } finally {
      setSaveAllLoading(false);
    }
  };

  const sshSettings = settingItems.filter((i) => i.category === "ssh");
  const repositorySettings = settingItems.filter(
    (i) => i.category === "repository",
  );
  const registrationSettings = settingItems.filter(
    (i) => i.category === "registration",
  );
  const chartSettings = settingItems.filter((i) => i.category === "chart");
  const dateSettings = settingItems.filter((i) => i.category === "date");

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            {gettext("System Settings")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {gettext(
              "Configure Git SSH keys, Git repository URLs, default registration access, chart visualization preferences, and date range selection limits.",
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            saveAllLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={handleSaveAll}
          disabled={saveAllLoading}
        >
          {gettext("Save All Settings")}
        </Button>
      </Box>

      {/* Git SSH Key Settings Section */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {gettext("Git SSH Key Settings")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Configure Git SSH public and private keys used for repository authentication and automation.",
        )}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {sshSettings.map((item) => (
          <Grid size={{ xs: 12, md: 6 }} key={item.key}>
            <SettingCard
              item={item}
              currentValue={settingsValues[item.key]}
              savingKey={savingKey}
              onValueChange={handleValueChange}
              onSaveSingle={saveSingleSetting}
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Git Repository Settings Section */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {gettext("Git Repository Settings")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Configure Git repository clone and remote URLs for WWW, App, and API codebases.",
        )}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {repositorySettings.map((item) => (
          <Grid size={{ xs: 12, md: 4 }} key={item.key}>
            <SettingCard
              item={item}
              currentValue={settingsValues[item.key]}
              savingKey={savingKey}
              onValueChange={handleValueChange}
              onSaveSingle={saveSingleSetting}
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Registration Settings Section */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {gettext("Registration Settings")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Default platform configuration for user registration and sign-up.",
        )}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {registrationSettings.map((item) => (
          <Grid size={{ xs: 12, md: 4 }} key={item.key}>
            <SettingCard
              item={item}
              currentValue={settingsValues[item.key]}
              savingKey={savingKey}
              onValueChange={handleValueChange}
              onSaveSingle={saveSingleSetting}
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Chart Settings Section */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {gettext("Chart Visualization Settings")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Default chart visual types for Energy, Climate, Cost, and Budget dashboards.",
        )}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {chartSettings.map((item) => (
          <Grid size={{ xs: 12, md: 4 }} key={item.key}>
            <SettingCard
              item={item}
              currentValue={settingsValues[item.key]}
              savingKey={savingKey}
              onValueChange={handleValueChange}
              onSaveSingle={saveSingleSetting}
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Date Range Limits Section */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        {gettext("Date Range Settings")}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Configure allowable past and future year selection limits (0 to 20 years) across system modules.",
        )}
      </Typography>
      <Grid container spacing={3}>
        {dateSettings.map((item) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.key}>
            <SettingCard
              item={item}
              currentValue={settingsValues[item.key]}
              savingKey={savingKey}
              onValueChange={handleValueChange}
              onSaveSingle={saveSingleSetting}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
